/**
 * The placement reticle — the ring that tracks across real surfaces before
 * the player commits to a spot.
 */

import * as THREE from 'three';

export interface Reticle {
  root: THREE.Group;
  setVisible(v: boolean): void;
  place(position: THREE.Vector3, quaternion: THREE.Quaternion): void;
  update(dt: number, time: number): void;
  dispose(): void;
}

export function buildReticle(): Reticle {
  const root = new THREE.Group();
  root.visible = false;

  const mat = new THREE.MeshBasicMaterial({
    color: 0x6ee7a5,
    transparent: true,
    opacity: 0.95,
    side: THREE.DoubleSide,
    depthTest: false,
  });

  const ringGeo = new THREE.RingGeometry(0.09, 0.105, 40);
  const ring = new THREE.Mesh(ringGeo, mat);
  ring.rotation.x = -Math.PI / 2;
  root.add(ring);

  // Pulsing inner ring gives the reticle a heartbeat, which reads as
  // "tracking and alive" rather than "frozen".
  const pulseGeo = new THREE.RingGeometry(0.03, 0.038, 32);
  const pulseMat = mat.clone();
  const pulse = new THREE.Mesh(pulseGeo, pulseMat);
  pulse.rotation.x = -Math.PI / 2;
  root.add(pulse);

  // Four ticks at the compass points to imply a grid on the surface.
  const tickGeo = new THREE.PlaneGeometry(0.012, 0.035);
  for (let i = 0; i < 4; i++) {
    const tick = new THREE.Mesh(tickGeo, mat);
    const a = (i / 4) * Math.PI * 2;
    tick.position.set(Math.cos(a) * 0.135, 0, Math.sin(a) * 0.135);
    tick.rotation.set(-Math.PI / 2, 0, -a);
    root.add(tick);
  }

  root.renderOrder = 999;

  return {
    root,
    setVisible(v) {
      root.visible = v;
    },
    place(position, quaternion) {
      // Smooth the tracked pose — raw hit-test results jitter enough to be
      // distracting at this scale.
      root.position.lerp(position, 0.35);
      root.quaternion.slerp(quaternion, 0.35);
    },
    update(_dt, time) {
      const t = (Math.sin(time * 2.4) + 1) / 2;
      pulse.scale.setScalar(1 + t * 1.4);
      pulseMat.opacity = 0.75 * (1 - t);
      ring.rotation.z = time * 0.35;
    },
    dispose() {
      ringGeo.dispose();
      pulseGeo.dispose();
      tickGeo.dispose();
      mat.dispose();
      pulseMat.dispose();
    },
  };
}
